import { LazyQueryExecFunction } from '@apollo/client';
import { DebouncedState } from 'use-debounce';
import * as yup from 'yup';

import {
  AttestationsListQuery,
  GetIsEmailFreeQuery,
  GetIsEmailFreeQueryVariables,
  VerifierType,
  VerifierTypeOfGovernment,
} from 'src/api';
import {
  addressFieldsValidationSchema,
  emailValidationSchema,
  passwordValidationSchema,
  phoneNumberValidationSchema,
  zipCodeValidationSchema,
} from 'src/constants/validation';
import { WEBSITE_REG_EXP } from 'src/constants/validationWebsite';

import { emailFreeErrorMessage } from '../components/constants';
import {
  CommercialVerifierBasicInfoFormFields,
  EmailConfirmationFormFields,
  GovernmentVerifierBasicInfoFormFields,
  InitialStepFormFields,
  InitialStepFormValues,
  NoticeToFurnishersFormFields,
  VerifierAccountTypeFormFields,
  VerifierBasicInfoFormFields,
  VerifierBasicInfoFormValues,
} from '../types';

type InitialStepFormValidationSchemaContext = {
  getIsEmailFree?: DebouncedState<
    LazyQueryExecFunction<GetIsEmailFreeQuery, GetIsEmailFreeQueryVariables>
  >;
};

export const noticeToFurnishersFormValidationSchema = yup.object({
  [NoticeToFurnishersFormFields.isRulesRead]: yup.boolean().required().isTrue('Required'),
});

export const initialStepFormValidationSchema = yup.object({
  [InitialStepFormFields.firstName]: yup.string().nullable().required('Required'),
  [InitialStepFormFields.lastName]: yup.string().nullable().required('Required'),
  [InitialStepFormFields.email]: emailValidationSchema
    .test(
      'is-email-free',
      emailFreeErrorMessage,
      async (value, context: yup.TestContext<InitialStepFormValidationSchemaContext>) => {
        const getIsEmailFree = context.options.context?.getIsEmailFree;

        const result =
          getIsEmailFree && value
            ? !(
                await getIsEmailFree({
                  variables: {
                    email: value,
                  },
                })
              )?.data?.getIsEmailFree.isEmailFree
            : true;

        return Boolean(result);
      },
    )
    .required('Required'),
  [InitialStepFormFields.phoneNumber]: phoneNumberValidationSchema.required('Required'),
  [InitialStepFormFields.password]: passwordValidationSchema.required('Required'),
  [InitialStepFormFields.passwordConfirm]: passwordValidationSchema
    .test((value, { options, createError }) => {
      const patchedOptions = options as yup.ValidateOptions<yup.AnyObject> & {
        parent: {
          password: string;
        };
      };

      const formValues: Partial<InitialStepFormValues> = patchedOptions.parent;

      const { password } = formValues;

      if (password !== value) {
        return createError({ message: 'Passwords must match' });
      }

      return true;
    })
    .required('Required'),
});

export const verifierAccountTypeFormValidation = {
  [VerifierAccountTypeFormFields.accountType]: yup
    .string()
    .nullable()
    .oneOf(Object.values(VerifierType))
    .required('Required'),
  [VerifierAccountTypeFormFields.permissiblePurposeReferenceId]: yup
    .string()
    .nullable()
    .required('Required'),
};

export const verifierAccountTypeFormValidationSchema = (
  items?: AttestationsListQuery['attestationList'],
) => {
  const validationStep = items?.reduce((acc, item) => {
    return { ...acc, [item.name]: yup.boolean().required().isTrue('Required') };
  }, {} as yup.ObjectShape);
  return yup.object({ ...validationStep, ...verifierAccountTypeFormValidation });
};

export const commercialVerifierBasicInfoFormValidationSchema = yup.object({
  [CommercialVerifierBasicInfoFormFields.registeredBusinessName]: yup
    .string()
    .nullable()
    .required('Required'),
});

export const emailConfirmationFormFieldsValidationSchema = yup.object({
  [EmailConfirmationFormFields.emailConfirmationCode]: yup.string().nullable().required('Required'),
});

export const governmentVerifierBasicInfoFormValidationSchema = yup.object({
  [GovernmentVerifierBasicInfoFormFields.governmentEntityName]: yup
    .string()
    .nullable()
    .required('Required'),
  [GovernmentVerifierBasicInfoFormFields.typeOfGovernment]: yup
    .string()
    .nullable()
    .oneOf(Object.values(VerifierTypeOfGovernment))
    .required('Required'),
});

export const verifierBasicInfoFormValidationSchema = yup
  .object({
    [VerifierBasicInfoFormFields.accountType]: yup.string().nullable().required('Required'),
    [VerifierBasicInfoFormFields.companyDisplayInfo]: yup.string().nullable().required('Required'),
    [VerifierBasicInfoFormFields.primaryContactEmail]: emailValidationSchema.required('Required'),
    [VerifierBasicInfoFormFields.zipCode]: zipCodeValidationSchema,
    [VerifierBasicInfoFormFields.primaryContactPhone]:
      phoneNumberValidationSchema.required('Required'),
    [VerifierBasicInfoFormFields.fein]: yup
      .string()
      .nullable()
      .test('invalid-fein', 'Invalid FEIN', value => !Number.isNaN(Number(value)))
      .length(9, 'Invalid FEIN')
      .required('Required'),
    [VerifierBasicInfoFormFields.commercialName]: yup.string().nullable(),
    [VerifierBasicInfoFormFields.industry]: yup
      .object({ value: yup.string() })
      .nullable()
      .required('Required'),
    [VerifierBasicInfoFormFields.websiteUrl]: yup
      .string()
      .nullable()
      .matches(WEBSITE_REG_EXP, 'Must be a valid URL')
      .required('Required'),
  })
  .concat(addressFieldsValidationSchema)
  .when({
    is: ({ accountType }: VerifierBasicInfoFormValues) => accountType === 'Commercial',
    then: schema => schema.concat(commercialVerifierBasicInfoFormValidationSchema),
    otherwise: schema => schema.concat(governmentVerifierBasicInfoFormValidationSchema),
  });
